// Renames a folder or a file where it is, in the drive or in Locked. A file opens with the part of
// its name before the extension selected, so typing replaces the name and keeps the kind, though
// the extension can still be changed on purpose. Leaving the name as it was closes without asking
// the API anything.
//
// A name something beside the item already has is refused in the API's own words; anything else
// that goes wrong asks to try again.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useCallback, useState} from 'react'
import {useMutation} from '@apollo/client/react'

import Dialog from '@mui/material/Dialog'
import RenameIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {RENAME_FOLDER} from '../../graphql/mutations/folders'
import {RENAME_UPLOAD} from '../../graphql/mutations/uploads'
import {FOLDERS} from '../../graphql/queries/folders'
import {LOCKED} from '../../graphql/queries/locked'
import {UPLOAD_LISTINGS} from '../../graphql/queries/uploads'
import {NAME_TAKEN, says} from '../../lib/errorTools'

import './nameDialog.scss'

// Where a file is kept is named by the folders above it, joined by slashes.
const PATH_SEPARATOR = '/'

const KINDS = {
  folder: {noun: 'folder', mutation: RENAME_FOLDER, listings: [FOLDERS]},
  file: {noun: 'file', mutation: RENAME_UPLOAD, listings: UPLOAD_LISTINGS}
}

// A leading dot starts a hidden file's name rather than an extension.
const stemLength = name => (name.lastIndexOf('.') > 0 ? name.lastIndexOf('.') : name.length)

const RenameDialog = ({entry, locked = false, onClose}) => {
  const {noun, mutation, listings} = KINDS[entry.kind]
  const [name, setName] = useState(entry.name)
  const [rename, {loading, error}] = useMutation(mutation, {refetchQueries: locked ? [LOCKED] : listings})
  const wanted = name.trim()
  const separated = name.includes(PATH_SEPARATOR)

  // A ref callback, since the dialog puts the field in place after this has mounted. Stable, so it
  // selects only once, rather than over what is being typed.
  const selectName = useCallback(input => {
    input?.setSelectionRange(0, entry.kind === 'file' ? stemLength(entry.name) : entry.name.length)
  }, [])

  const handleSubmit = event => {
    event.preventDefault()
    if (!wanted || separated) return
    if (wanted === entry.name) return onClose()

    rename({variables: {input: {id: entry.id, name: wanted}}})
      .then(onClose)
      // Shown from the mutation's own error state below.
      .catch(() => {})
  }

  const failure = says(error, NAME_TAKEN) ? NAME_TAKEN : `That ${noun} couldn’t be renamed. Try again.`

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='rename-title'
      slotProps={{paper: {className: classnames('name-dialog', {locked}), component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <RenameIcon />
        <h2 className='dialog-title' id='rename-title'>Rename {noun}</h2>
      </div>

      <label className='dialog-field'>
        <span className='field-label'>New name</span>
        <input
          ref={selectName}
          type='text'
          value={name}
          autoComplete='off'
          autoFocus
          onChange={event => setName(event.target.value)}
        />
      </label>
      {separated && <Text className='dialog-error' role='alert'>Names can’t contain “{PATH_SEPARATOR}”.</Text>}
      {error && !separated && <Text className='dialog-error' role='alert'>{failure}</Text>}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-create' type='submit' disabled={loading || separated || !wanted}>Rename</BaseButton>
      </div>
    </Dialog>
  )
}

RenameDialog.propTypes = {
  entry: PropTypes.shape({
    kind: PropTypes.oneOf(Object.keys(KINDS)).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  // In Locked, which must be open.
  locked: PropTypes.bool,
  onClose: PropTypes.func.isRequired
}

export default RenameDialog
